import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  Download,
  File as FileIcon,
  FileX,
  Clock,
  UploadCloud,
} from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { formatBytes, formatTimeLeft, formatAbsolute } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DownloadPage = () => {
  const { slug } = useParams();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchFile = async () => {
      try {
        const response = await axios.get(`${API}/files/${slug}`);
        if (!cancelled) setFile(response.data);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFile();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <Layout>
      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center px-4 py-12 sm:px-6">
        {loading ? (
          <Card className="rounded-2xl border-border/70 shadow-xl shadow-primary/5">
            <CardContent className="p-8">
              <Skeleton className="mx-auto h-16 w-16 rounded-2xl" />
              <Skeleton className="mx-auto mt-6 h-5 w-3/4" />
              <Skeleton className="mx-auto mt-3 h-4 w-1/2" />
              <Skeleton className="mt-8 h-12 w-full rounded-xl" />
            </CardContent>
          </Card>
        ) : notFound ? (
          <Card className="rounded-2xl border-border/70 shadow-xl shadow-primary/5">
            <CardContent className="p-8 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                <FileX className="h-8 w-8" />
              </div>
              <h1 className="mt-6 text-xl font-semibold">
                Link expired or not found
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This file is no longer available. It may have expired and been
                deleted, or the link was mistyped.
              </p>
              <Button asChild className="mt-8 h-11 w-full rounded-xl">
                <Link to="/">
                  <UploadCloud className="mr-2 h-4 w-4" />
                  Share your own files
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-2xl border-border/70 shadow-xl shadow-primary/5">
            <CardContent className="p-8 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <FileIcon className="h-8 w-8" />
              </div>
              <h1 className="mt-6 break-words text-xl font-semibold">
                {file.original_filename}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {formatBytes(file.size)}
              </p>
              <p className="mt-1 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                Expires in {formatTimeLeft(file.expires_at)} (
                {formatAbsolute(file.expires_at)})
              </p>
              <Button
                asChild
                className="mt-8 h-12 w-full rounded-xl text-base font-semibold shadow-lg shadow-primary/20"
              >
                <a href={`${API}/files/${slug}/download`}>
                  <Download className="mr-2 h-5 w-5" />
                  Download
                </a>
              </Button>
              <p className="mt-4 text-xs text-muted-foreground">
                Downloaded {file.download_count} time
                {file.download_count === 1 ? "" : "s"} so far
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default DownloadPage;
